import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ArrowBackOutline, Play } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import {
  Box,
  Button,
  DeprecatedIconButton,
  DividerSeparator,
  LinkButton,
  Stack,
  Tooltip,
  Typography,
  useSnackbar,
} from '@lyearn/ui';
import { DividerProps } from '@lyearn/ui';
import EditableText from '@/components/EditableText';
import PublishChangesPopover from '@/components/EditorBuilder/Settings/Dialogs/PublishChanges/PublishChangesPopover';
import { onPublishType } from '@/components/EditorBuilder/Settings/types';
import { Link } from '@/components/Link';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import WorkspaceAccessControlDialog from '@/components/WorkspaceAccessControl/components/WorkspaceAccessControlDialog';
import { FeatureFlags } from '@/helper/FeatureFlags';
import { useFeatureFlags } from '@/helper/FeatureFlags';
import useNavigation from '@/hooks/useNavigation';
import ContentEditorSettings from '@/modules/corporate/components/ContentEditorSettings';
import StatusLabel from '@/modules/corporate/modules/admin/views/LearningContent/components/ListItemCard/StatusLabel';
import { useEmbeddedSurveyEditor } from '@/modules/corporate/modules/course/components/SurveyCardContainer/components/SurveyEditorModal/context';
import { useSyncLocalizedContentMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/syncLocalizedContent';
import { useVerifyLocalizedContentMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/verifyLocalizedContent';
import { useLocalizedContentsQuery } from '@/modules/corporate/modules/course/graphql/queries/generated/localizedContents';
import LanguageSelector from '@/modules/corporate/modules/course/views/CourseBuilder/components/LanguageSelector';
import SavingState from '@/modules/corporate/modules/quiz/views/QuizEditor/components/QuizEditorHeader/components/SavingState';
import routes from '@/pages/corporate/routes/contents/survey';
import {
  ResourceStatusEnum,
  ResourceTypeEnum,
  SurveyContentType,
  UpdateSurveyInput,
  WorkspaceActions,
  WorkspaceSubjects,
} from '@/types/schema';

import useSurveyEditor from '../../hooks/useSurveyEditor';
import { SurveyEditorActionEnum } from '../../hooks/useSurveyEditor/types';
import useSurveyLanguageInfo from './hooks/useSurveyLanguageInfo';
import { breadcrumbStyles } from './styles';

const DIVIDER_PROPS: DividerProps = { className: 'h-28 bg-bg-tertiary', orientation: 'vertical' };

// TODO: common out saving utils
function SurveyEditorHeader({
  onPublish: _onPublish,
  onRevertSuccess,
  showPublishPopover,
  onPublishSubmit,
  onClose,
  isSaving,
  survey,
  initialSurvey,
  onAction,
  allowMinorVersionPublish,
}: {
  onPublish: () => void;
  onRevertSuccess: () => void;
  showPublishPopover?: boolean;
  onPublishSubmit: onPublishType;
  onClose: () => void;
  survey: UpdateSurveyInput;
} & Pick<
  ReturnType<typeof useSurveyEditor>,
  'isSaving' | 'initialSurvey' | 'onAction' | 'allowMinorVersionPublish'
>) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const embeddedEditor = useEmbeddedSurveyEditor();
  const [isPublishing, { on, off }] = useToggle(false);
  const title = survey.name || t('SurveyEditor:UntitledSurvey')!;
  const { [FeatureFlags.EnableMultiLanguageContent]: isMultiLanguageContentEnabled } =
    useFeatureFlags();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [isEditable, { toggle: toggleEditable }] = useToggle(false);
  const [{ fetching: isSyncing }, syncLocalizedContent] = useSyncLocalizedContentMutation();

  const { isLocalizedContent, isSyncedContentVerified, id, status } = initialSurvey ?? {};
  const [{ fetching: isVerifying }, verifyLocalizedContent] = useVerifyLocalizedContentMutation();
  const [canBeVerified, setCanBeVerified] = useState(
    isLocalizedContent && isSyncedContentVerified === false,
  );
  const [disableVerified, setDisableVerified] = useState(
    isLocalizedContent && isSyncedContentVerified === true,
  );
  const {
    data: surveyLanguageData,
    refetch: refetchSurveyLanguageData,
    fetching: fetchingSurveyLanguageData,
    jobStatus,
  } = useSurveyLanguageInfo({
    contentId: survey.id,
  });

  const localizedContentIds = useMemo(() => {
    if (surveyLanguageData?.languageConfigs?.length) {
      return surveyLanguageData.languageConfigs.map((config) => config.contentId);
    } else {
      return initialSurvey?.languageConfigs?.map((config) => config.contentId) || [];
    }
  }, [surveyLanguageData, initialSurvey]);

  const [localizedContents, refetchLocalizedContent] = useLocalizedContentsQuery({
    variables: { filters: { ids: localizedContentIds } },
    pause: !initialSurvey || !localizedContentIds?.length || initialSurvey?.isLocalizedContent,
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (isPublishing && !isSaving) {
      off();
    }
  }, [isPublishing, off, isSaving]);

  const onPublish = useCallback(() => {
    refetchLocalizedContent?.();
    on();
    _onPublish();
  }, [_onPublish, on, refetchLocalizedContent]);

  const onBack = useCallback(
    () => (embeddedEditor ? embeddedEditor.onClose() : navigation.back()),
    [embeddedEditor, navigation],
  );

  const onRename = useCallback(
    (_: any, updatedName: any) => {
      if (title !== updatedName) {
        onAction({
          type: SurveyEditorActionEnum.UpdateSurvey,
          payload: { updates: { name: updatedName } },
        });
      }
      toggleEditable();
    },
    [onAction, title, toggleEditable],
  );
  const showSyncNowButton = useMemo(() => {
    if (isLocalizedContent && surveyLanguageData) {
      // @ts-ignore
      const lastContentEditTimeAt = surveyLanguageData.sourceContent?.lastContentEditTimeAt;
      const { sourceContentSyncedAt } = surveyLanguageData;
      const isSynced =
        lastContentEditTimeAt &&
        sourceContentSyncedAt &&
        new Date(sourceContentSyncedAt as string).getTime() >
          new Date(lastContentEditTimeAt as string).getTime();
      return !isSynced;
    }
    return false;
  }, [surveyLanguageData, isLocalizedContent]);

  const allBlocksNotTranslated =
    localizedContentIds?.length > 0 &&
    !localizedContents?.data?.contents?.edges
      ?.filter((edge: any) => edge.node?.status !== ResourceStatusEnum.Draft)
      ?.every((edge) => (edge?.node as SurveyContentType)?.isSyncedContentVerified) &&
    !isLocalizedContent;

  const isMainContentPublishDisabled = !isLocalizedContent && allBlocksNotTranslated;
  useEffect(() => {
    if (isMainContentPublishDisabled && showPublishPopover) {
      onClose();
    }
  }, [isMainContentPublishDisabled, showPublishPopover, onClose]);

  const onSync = useCallback(async () => {
    const { sourceContent, id } = initialSurvey ?? {};
    await syncLocalizedContent({
      data: {
        entityId: (sourceContent as SurveyContentType)?.id,
        localizedEntityId: id,
      },
    }).then((res) => {
      if (res.data?.syncLocalizedContent.success) {
        refetchSurveyLanguageData?.();
        showSnackbar({
          message: t('Course:SyncLocalizedContentSuccess'),
        });
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
    });
  }, [
    initialSurvey,
    syncLocalizedContent,
    showSnackbar,
    t,
    showErrorSnackbar,
    refetchSurveyLanguageData,
  ]);

  const onVerifyClick = useCallback(async () => {
    if (!id) return;
    await verifyLocalizedContent({
      data: {
        entityId: id,
      },
    }).then((result) => {
      if (result.data?.verifyLocalizedContent.success) {
        setCanBeVerified(false);
        setDisableVerified(true);
        showSnackbar({
          message: t('Course:LocalizedContentVerified'),
        });
      } else {
        showErrorSnackbar({
          message: t('Common:SomethingWentWrong'),
        });
      }
    });
  }, [
    id,
    showErrorSnackbar,
    showSnackbar,
    t,
    verifyLocalizedContent,
    setDisableVerified,
    setCanBeVerified,
  ]);

  const renderActions = useCallback(() => {
    if (isLocalizedContent && status !== ResourceStatusEnum.Draft) {
      return (
        <Button
          className="h-40 me-8"
          data-id="submit"
          disabled={disableVerified || isSyncing}
          loading={isVerifying || isSyncing}
          size="small"
          variant="filled"
          onClick={showSyncNowButton ? onSync : onVerifyClick}>
          {showSyncNowButton ? t('Common:Actions.SyncNow') : t('Common:Actions.Verify')}
        </Button>
      );
    }
    const ActionButton = (
      <Button
        disabled={(isPublishing && isSaving) || allBlocksNotTranslated}
        loading={isPublishing}
        onClick={onPublish}>
        {t('Common:Actions.Publish')}
      </Button>
    );

    return (
      <PublishChangesPopover
        allBlocksNotTranslated={allBlocksNotTranslated}
        allowMinorVersionPublish={allowMinorVersionPublish}
        contentType={ResourceTypeEnum.Survey}
        disabled={isPublishing}
        id={survey.id}
        open={!!showPublishPopover}
        onClose={onClose}
        onPublish={onPublishSubmit}>
        {ActionButton}
      </PublishChangesPopover>
    );
  }, [
    allowMinorVersionPublish,
    allBlocksNotTranslated,
    isPublishing,
    isSaving,
    onClose,
    onPublish,
    onPublishSubmit,
    showPublishPopover,
    survey.id,
    isLocalizedContent,
    status,
    canBeVerified,
    disableVerified,
    showSyncNowButton,
    isSyncing,
    onSync,
    t,
  ]);

  usePageLayout({
    title,
    LeftNode: (
      <Stack alignItems="center" gapX={12}>
        <Stack alignItems="center">
          <LinkButton className="group flex-shrink-0 py-2 px-4" onClick={onBack}>
            <ArrowBackOutline className="fill-current text-icon-primary group-hover:text-icon-active rtl:rotate-180" />
          </LinkButton>
          <Box
            className="flex h-40 w-auto cursor-pointer items-center rounded-8 border border-transparent ms-2 hover:border-border-dark"
            data-id="title"
            onClick={toggleEditable}>
            {isEditable ? (
              <EditableText className="w-256" name={title} onAction={onRename} />
            ) : (
              <Typography
                className={cn('break-all px-10', breadcrumbStyles)}
                color="text-text-primary"
                numberOfLines={1}
                variant="body-short-02">
                {title}
              </Typography>
            )}
          </Box>
        </Stack>
        <StatusLabel status={initialSurvey?.status} />
      </Stack>
    ),

    RightNode: (
      <DividerSeparator
        alignItems="center"
        dividerProps={isSaving ? DIVIDER_PROPS : undefined}
        gapX={12}>
        <SavingState isSaving={isSaving} />
        <Stack alignItems="center" gapX={8}>
          {!surveyLanguageData || !isMultiLanguageContentEnabled || true ? null : (
            <LanguageSelector
              className="mr-8"
              content={surveyLanguageData}
              jobStatus={jobStatus}
              localizedContents={localizedContents?.data}
              refetchLocalizedContent={refetchLocalizedContent}
              refetchQuery={refetchSurveyLanguageData}
            />
          )}
          <ContentEditorSettings
            content={initialSurvey as SurveyContentType}
            itemHeader={t('Common:ResourceTypeSM.Survey')}
            onRevertSuccess={onRevertSuccess}
          />
          <Link data-id="preview" to={routes.SurveyPreviewDevices(initialSurvey?.id!)}>
            <Tooltip title={`${t('Common:Actions.Preview')}`}>
              <DeprecatedIconButton appearance="secondary" size="small" variant="outlined">
                <Play className="h-20 w-20 fill-current text-icon-active" />
              </DeprecatedIconButton>
            </Tooltip>
          </Link>
          <WorkspaceAccessControlDialog
            action={WorkspaceActions.Publish}
            fallback={<Button>{t('Common:Actions.Publish')}</Button>}
            subject={WorkspaceSubjects.Resource}>
            {renderActions()}
          </WorkspaceAccessControlDialog>
        </Stack>
      </DividerSeparator>
    ),
  });

  return null;
}

export default SurveyEditorHeader;
